import queryKeys from '@/constants/queryKeys';
import { postGenerateCortexOrganizationInsights } from '@/services/api/requests/cortex';
import { TInsightGenerationStatus } from '@/types/Cortex.types';
import { useQuery } from '@tanstack/react-query';

// While the AI is (re)generating, the endpoint returns the stale cached snapshot with
// status PROCESSING/PENDING — poll so the fresh result swaps in without a manual refresh.
const INSIGHTS_POLL_MS = 15_000;
const GENERATING_STATUSES: readonly TInsightGenerationStatus[] = ['PROCESSING', 'PENDING'];

const useCortexOrganizationInsights = (orgId: string) => {
  const { isLoading, data, isError, error, isRefetching, refetch, isSuccess } = useQuery({
    queryKey: [queryKeys.cortex.organizationInsights, orgId],
    enabled: !!orgId,
    queryFn: async () => postGenerateCortexOrganizationInsights(orgId),
    // Stop polling once generation finishes (COMPLETED/FAILED) or if we're rate-limited.
    refetchInterval: (query) => {
      const latest = query.state.data;
      if (!latest || latest.rateLimited) return false;
      return GENERATING_STATUSES.includes(latest.status) ? INSIGHTS_POLL_MS : false;
    },
    refetchIntervalInBackground: false,
  });

  const isInsightsGenerating = !!data && GENERATING_STATUSES.includes(data.status);

  return { isLoading, data, isError, error, isRefetching, refetch, isSuccess, isInsightsGenerating };
};

export default useCortexOrganizationInsights;
